import { useState, useEffect } from "react";
import { LABELS, BUTTON_LABEL, toastError, toastSuccess } from "../../constants";
import ImageLightbox from "../ImageLightbox";
import SimpleButton from "../Button/Button";
import CustomModal from "../CustomModal/CustomModal";
import moment from "moment";
import { DATE_FORMAT } from "../../constants/resource";
import { useImageDeleteMutation } from "../../../Features/order/order-slice";

interface ImageItem {
  _id?: string;
  image?: string | { _id: string };
  fileName?: string;
  imageUrl?: string;
  thumbnailUrl?: string;
  sizeBytes?: number;
  uploadedBy?: { _id: string; name?: string; firstName?: string; lastName?: string };
  uploadedAt?: string;
  isPurged?: boolean;
}

interface ImageGalleryProps {
  referenceImages: ImageItem[];
  orderId: string;
  backendUrl: string;
  canDelete?: boolean;
  onImageDeleted?: () => void;
}

const ImageGallery = ({
  referenceImages,
  orderId,
  backendUrl,
  canDelete = false,
  onImageDeleted,
}: ImageGalleryProps) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);

  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [openMenuId]);

  const [imageDelete] = useImageDeleteMutation();
  const [isDownloadAllLoading, setIsDownloadAllLoading] = useState(false);

  const safeReferenceImages = Array.isArray(referenceImages) ? referenceImages : [];
  const activeImages = safeReferenceImages.filter((img) => !img?.isPurged);

  const getImageUrl = (item: ImageItem, useThumbnail: boolean = true): string => {
    const url = useThumbnail ? item.thumbnailUrl : item.imageUrl;
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${backendUrl}${url}`;
  };

  const getUploadedByName = (item: ImageItem): string => {
    const user = item.uploadedBy;
    if (!user) return "-";
    if (user.name) return user.name;
    if (user.firstName || user.lastName) {
      return `${user.firstName || ""} ${user.lastName || ""}`.trim();
    }
    return "-";
  };

  const handleDownloadAll = async () => {
    setIsDownloadAllLoading(true);
    try {
      const token = localStorage.getItem("x-access-token");
      const apiUrl = `${backendUrl}/api/${orderId ? "images/download-all/" + orderId : ""}`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "x-access-token": token || "",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.errors?.[0]?.msg || "Failed to download images"
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `order-images-${orderId}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toastSuccess("Images downloaded successfully");
    } catch (err: any) {
      toastError(err.message || "Failed to download images");
    } finally {
      setIsDownloadAllLoading(false);
    }
  };

  const handleSingleDownload = async (img: ImageItem) => {
    setOpenMenuId(null);
    const imageUrl = getImageUrl(img, false);
    if (!imageUrl) return;

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = img.fileName || "reference-image.jpg";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const a = document.createElement("a");
      a.href = imageUrl;
      a.download = img.fileName || "reference-image.jpg";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const confirmDeleteImage = async () => {
    if (!imageToDelete) return;
    setDeleteLoading(imageToDelete);
    try {
      const response = await imageDelete(imageToDelete).unwrap();
      if (response?.status) {
        toastSuccess(response.message || "Image deleted");
        onImageDeleted?.();
      }
    } catch (err: any) {
      toastError(err?.data?.errors?.[0]?.msg || "Failed to delete image");
    } finally {
      setDeleteLoading(null);
      setImageToDelete(null);
    }
  };

  const openLightbox = (index: number) => {
    // Find the index within active (non-purged) images
    const activeIndex = activeImages.indexOf(safeReferenceImages[index]);
    if (activeIndex >= 0) {
      setLightboxIndex(activeIndex);
    }
  };

  if (!safeReferenceImages || safeReferenceImages.length === 0) {
    return (
      <>
        <h6 className="my-3 section-title">{LABELS.REFERENCE_IMAGES_TEXT}</h6>
        <div className="card section-card mb-3">
          <div className="card-body">
            <p className="text-muted mb-0">{LABELS.NO_IMAGES_UPLOADED_TEXT}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <h6 className="my-3 section-title">
        {LABELS.REFERENCE_IMAGES_TEXT}
        {activeImages.length > 0 && (
          <span className="ms-2">
            <SimpleButton
              className="btn theme-btn theme-btn-sm"
              onClick={handleDownloadAll}
              text={
                isDownloadAllLoading ? (
                  <span className="spinner-border spinner-border-sm" role="status" />
                ) : (
                  LABELS.DOWNLOAD_ALL_IMAGES_TEXT
                )
              }
              disabled={isDownloadAllLoading}
            />
          </span>
        )}
      </h6>
      <div className="card section-card mb-3">
        <div className="card-body">
          <div className="image-gallery-row">
            {safeReferenceImages.map((img, index) => {
              const isPurged = img.isPurged;
              const imageId =
                typeof img.image === "object" && img.image?._id
                  ? img.image._id
                  : typeof img.image === "string"
                    ? img.image
                    : img._id || "";

              return (
                <div className="image-gallery-col" key={imageId || index}>
                  <div className="image-gallery-card">
                    <div
                      className="image-gallery-thumbnail"
                      onClick={() => !isPurged && openLightbox(index)}
                      style={{ cursor: isPurged ? "default" : "pointer" }}
                    >
                      {!isPurged && (
                        <div
                          className="image-gallery-menu-wrapper"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="image-gallery-menu-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(openMenuId === imageId ? null : imageId);
                            }}
                            title="Options"
                          >
                            &#8942;
                          </button>
                          {openMenuId === imageId && (
                            <div className="image-gallery-dropdown-menu">
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuId(null);
                                  openLightbox(index);
                                }}
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="me-2"
                                >
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                                {BUTTON_LABEL.VIEW || "View"}
                              </button>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSingleDownload(img);
                                }}
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="me-2"
                                >
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                  <polyline points="7 10 12 15 17 10" />
                                  <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                {BUTTON_LABEL.DOWNLOAD || "Download"}
                              </button>
                              {canDelete && (
                                <button
                                  type="button"
                                  className="dropdown-item text-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuId(null);
                                    setImageToDelete(imageId);
                                  }}
                                  disabled={deleteLoading === imageId}
                                >
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="me-2"
                                  >
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                  </svg>
                                  {deleteLoading === imageId ? "Deleting..." : BUTTON_LABEL.DELETE}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {isPurged ? (
                        <div className="image-gallery-expired">
                          <span>{LABELS.IMAGES_EXPIRED_TEXT}</span>
                        </div>
                      ) : (
                        <img
                          src={getImageUrl(img, true)}
                          alt={img.fileName || `Reference ${index + 1}`}
                          loading="lazy"
                          onError={(e) => {
                            // If thumbnail fails, try full image
                            const target = e.target as HTMLImageElement;
                            if (target.src !== getImageUrl(img, false)) {
                              target.src = getImageUrl(img, false);
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {lightboxIndex !== null && activeImages.length > 0 && (
        <ImageLightbox
          images={activeImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() =>
            setLightboxIndex(Math.max(0, lightboxIndex - 1))
          }
          onNext={() =>
            setLightboxIndex(
              Math.min(activeImages.length - 1, lightboxIndex + 1)
            )
          }
          backendUrl={backendUrl}
        />
      )}

      {imageToDelete && (
        <CustomModal
          show={!!imageToDelete}
          close={() => setImageToDelete(null)}
          size="md"
          message="Are you sure you want to delete this image?"
          modalTitle="Confirm Delete"
          modalButton={[
            {
              text: BUTTON_LABEL.CANCEL || "Cancel",
              action: () => setImageToDelete(null),
              className: "btn theme-btn btn-inverse me-2",
              isLoading: false,
            },
            {
              text: BUTTON_LABEL.DELETE || "Delete",
              action: confirmDeleteImage,
              className: "btn theme-btn",
              isLoading: deleteLoading === imageToDelete,
            },
          ]}
          fullscreen={false}
          className=""
          centerClass=""
          modalTitleIcon=""
          footerClass="justify-content-end"
          messageClass="text-start"
        >
          {null}
        </CustomModal>
      )}
    </>
  );
};

export default ImageGallery;
